import { get, resolveFieldLabel } from '@/lib/intakeFields';
import type { ApplicationScreen, FieldSpec, SectionKey } from '@/lib/intakeFields';
import { useTranslation } from 'react-i18next';
import { formatFieldValue } from '@/components/intake/EditableFieldRow';
import { MONEY_ANSWER_PATHS, answerCurrencySymbol, formatMoneyAnswer } from '@/lib/moneyAnswer';
import { useLocale } from '@/i18n/useLocale';
import { MonoLabel, SectionTitle } from '@/components/editorial';
import { useCopy } from '@/hooks/useCopy';
import type { ReactNode } from 'react';
import { ShownInEnglish } from './FormLanguageNotice';

/**
 * The family's own answers, read-only, above the final consent (#1114).
 *
 * WHY IT EXISTS. Step 13 was called "Review & submit" and contained only the
 * data-sharing gate: ticking the box submitted ~90 answers the family had no
 * way to re-read except by paging backwards through eleven screens. TRF's
 * ruling (2026-08-29, A2) is that the review is real and sits ABOVE the
 * checkbox, so a family reads before it agrees rather than after.
 *
 * WHAT IT RENDERS, AND WHAT IT REFUSES TO. Only what the family typed into
 * this wizard: `sectionData` (#2800: the grant ask is gone). Never `flagged_fields`, never a
 * decision, never anything staff derived — this is the family's own data on
 * the family's own screen, and that is exactly the property that keeps it from
 * being a new PHI surface.
 *
 * WHAT WAS UPLOADED IS READ BACK TOO (#2728). The intro promises "everything
 * you told us", and a bill uploaded as the diagnosis letter, or a missing
 * family photo, is exactly what a family should catch here rather than staff
 * catching it after submit. Only FILE NAMES, per document slot — never the
 * file itself — so the surface stays the family's own words and no new PHI.
 * An empty slot shows the same em dash as an unanswered question.
 *
 * IT WALKS THE STAMPED SCREENS, NOT TODAY'S FIELD LIST. `screens` is the walk
 * the wizard itself built from the version pinned at mount — the same version
 * whose number is stamped on this submission. So the questions, their order,
 * their group headings and their option wording are the ones this application
 * is actually being made against, by construction rather than by care. The rule
 * reaches one level down through `formatFieldValue` → `optionLabelFor`, whose
 * stamped `optionLabels` are primary (B-431), so an option TRF has since
 * retired still reads as the words the family clicked rather than its slug.
 *
 * A RETIRED QUESTION'S ANSWER IS NOT SILENTLY DROPPED. `toApplicationScreens`
 * (and `fieldIndex` under it) removes every retired field from the walk, which
 * is right for ASKING and wrong for READING BACK: a family that answered a
 * question TRF has since retired — on an earlier visit, before a republish —
 * would watch its own answer disappear from the one screen that promises "this
 * is everything you told us", while staff go on reading it (D-SE-13). So
 * `retiredFields` carries the other half of the index and the tail section
 * renders every retired question that HAS a stored answer, exactly the rule the
 * staff drawer applies: retire, never destroy; an empty retired field renders
 * nothing at all. A question TRF ADDED since is a different case and correctly
 * absent — the family was never asked it and has no answer to re-read.
 *
 * AN UNANSWERED QUESTION KEEPS ITS ROW. `formatFieldValue` renders an em dash
 * for an empty value and the row is never dropped. Hiding blanks would make
 * the one screen built for spotting a gap the one screen that cannot show one.
 * (The retired tail is the one exception, and for the opposite reason: a blank
 * row for a question nobody is asking any more is noise, not a gap to fill.)
 *
 * GUARDRAIL 7. The two prose strings are held until the copy read has settled.
 * A family resuming straight onto this step from a cold cache would otherwise
 * read the seed and watch it swap for the published wording mid-sentence.
 *
 * The formatter is IMPORTED from the staff row rather than copied (D-SE-12,
 * "one formatter, reused"): the file-answer, date-zone (B-432) and option-label
 * rules are the ones already proven on the staff surfaces, and a second copy is
 * a second place for a family and a case manager to read the same answer
 * differently.
 */
interface ReviewSummaryProps {
  /** The stamped screens, in the order the wizard walked them. */
  screens: ApplicationScreen[];
  /** The family's draft answers, per storage section. */
  sectionData: Record<SectionKey, Record<string, unknown>>;
  /** The stamped field index — the wizard's own `findFieldSpec`. */
  findFieldSpec: (section: SectionKey | string, key: string) => FieldSpec | undefined;
  /**
   * The stamped version's RETIRED fields, per storage section —
   * `sectionFieldsFrom(version, { retired: true })`. Only those with a stored
   * answer are rendered; a family with none never sees the tail at all.
   */
  retiredFields?: Record<string, FieldSpec[]>;
  /** True for a retired row (`section.key`) whose label is English on a
   *  translated form (`localizeVersion`'s `retiredInEnglish`); it gets the
   *  "shown in English" mark. */
  retiredShownInEnglish?: (path: string) => boolean;
  /**
   * #2728: the documents step, read back as one row per slot (the wizard's
   * own `docSlots`, so labels and order match the step) with the file names
   * the family uploaded into it. Absent → no documents section, as before.
   */
  documents?: ReviewDocuments;
}

export interface ReviewDocuments {
  /** The section heading — the documents step's own name. */
  heading: string;
  slots: ReadonlyArray<{ kind: string; label: string }>;
  uploaded: ReadonlyArray<{ kind: string; filename: string }>;
}

/** One row per slot: its label and the names of the files in it, in upload
 *  order. An empty slot keeps its row (an em dash on screen). */
export function documentReviewRows(documents: ReviewDocuments): Array<{ kind: string; label: string; files: string[] }> {
  return documents.slots.map((slot) => ({
    kind: slot.kind,
    label: slot.label,
    files: documents.uploaded.filter((doc) => doc.kind === slot.kind).map((doc) => doc.filename),
  }));
}

/** A retired label's trailing "(retired)" is staff vocabulary: the tail's own
 *  heading already says, in the family's language, that TRF no longer asks
 *  these (2026-10-11, c06-13). */
const RETIRED_SUFFIX = /\s*\(retired\)\s*$/i;

function AnswerRow({
  field, siblings, sectionValues, value, path, testId, labelSuffix,
}: {
  field: FieldSpec;
  siblings: Record<string, unknown> | undefined;
  /** #2523: every section's answers, for a label worded off another section
   *  (a Canadian family's questions read back in Canadian wording). */
  sectionValues?: Record<string, Record<string, unknown> | undefined>;
  value: unknown;
  /** `<section>.<field>` — #2948: a money answer reads as money ("$350"). */
  path: string;
  testId: string;
  /** Rendered after the label inside the term (the "shown in English" mark). */
  labelSuffix?: ReactNode;
}) {
  // #2109 R4-B29: "Yes", "No", "Uploaded file" and dates in the reader's language.
  const { t } = useTranslation();
  const { bcp47 } = useLocale();
  return (
    <div className="flex flex-col gap-0.5 border-b border-border py-2 last:border-b-0 sm:flex-row sm:gap-4">
      <dt className="text-body text-stevens-ink-3 sm:w-1/2">
        {resolveFieldLabel(field, siblings, sectionValues)}
        {labelSuffix}
      </dt>
      {/* `whitespace-pre-line`: `household.members` and `intake.family_story`
          are textareas that ask for a list or a narrative, and the family typed
          line breaks into them. Collapsed, the answers most worth re-reading
          are the ones hardest to re-read. */}
      <dd
        className="text-body font-medium text-foreground whitespace-pre-line sm:w-1/2"
        data-testid={testId}
      >
        {/* #2948: a money answer reads as money, by the staff page's own rule
            (`$` or `CA$` by the family's country); the phone stays as typed. */}
        {(MONEY_ANSWER_PATHS.has(path)
          && formatMoneyAnswer(value, answerCurrencySymbol(sectionValues?.intake?.country) ?? '$', bcp47))
          || formatFieldValue(value, field, bcp47, t)}
      </dd>
    </div>
  );
}

/** A group heading — the 11px label rung, which is what group labels are
 *  (grammar §2 v3.2). The asking screen renders these as serif card titles, but
 *  there the group IS the section; here the screen above it already holds that
 *  rung and a second one at the same size would flatten the hierarchy. */
function GroupLabel({ children }: { children: string }) {
  return (
    <h4 className="m-0">
      <MonoLabel tone="ink">{children}</MonoLabel>
    </h4>
  );
}

export function ReviewSummary({
  screens, sectionData, findFieldSpec, retiredFields = {}, retiredShownInEnglish, documents,
}: ReviewSummaryProps) {
  const { copy, ready } = useCopy();

  // Every retired question this family actually answered, in the stamped
  // version's own field order (`sectionFieldsFrom` sorts by it), flattened
  // across sections — the tail is a footnote, not a second walk.
  const retiredAnswered = Object.entries(retiredFields).flatMap(([section, fields]) =>
    fields
      .filter((field) => formatFieldValue(get(sectionData[section as SectionKey], field.key), field) !== '—')
      .map((field) => ({ section, field: { ...field, label: field.label.replace(RETIRED_SUFFIX, '') } })));

  // Guardrail 7: hold the paint until the copy read has SETTLED — succeeded or
  // failed, either is an answer and neither takes more than one attempt. In the
  // ordinary walk the previous step (documents) has already warmed the same
  // query key, so this is only ever seen on a resume that lands here directly.
  if (!ready) {
    return (
      <div
        className="py-6"
        role="status"
        aria-live="polite"
        data-testid="review-summary-loading"
      >
        <MonoLabel className="block font-normal">Loading…</MonoLabel>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="review-summary">
      <div className="space-y-1">
        <SectionTitle level="title">{copy('apply.review.heading')}</SectionTitle>
        <p className="text-body text-stevens-ink-3">{copy('apply.review.intro')}</p>
      </div>

      {screens.map((screen) => (
        <section key={screen.id} className="space-y-3" data-testid={`review-section-${screen.id}`}>
          <SectionTitle level="card-title">{screen.label}</SectionTitle>
          {screen.groups.map((group, gi) => {
            // Mirrors the wizard's own walk: a ref the stamped definition has
            // no field for is not rendered, on the summary as on the screen —
            // and a group left with nothing takes its heading with it.
            const rows = group.fields.flatMap((ref) => {
              const field = findFieldSpec(ref.section, ref.key);
              return field ? [{ ref, field }] : [];
            });
            if (!rows.length) return null;
            return (
              <div key={group.label ?? `group-${gi}`} className="space-y-1">
                {/* The asking screen heads this same group with this same
                    label (ApplicationWizard). Flattened away, a screen the
                    family answered as four labelled groups reads back as one
                    undivided list, and two plainly-labelled fields in
                    different groups become indistinguishable. */}
                {group.label && <GroupLabel>{group.label}</GroupLabel>}
                <dl className="m-0">
                  {rows.map(({ ref, field }) => (
                    <AnswerRow
                      key={`${ref.section}.${field.key}`}
                      field={field}
                      siblings={sectionData[ref.section]}
                      sectionValues={sectionData}
                      value={get(sectionData[ref.section], field.key)}
                      path={`${ref.section}.${field.key}`}
                      testId={`review-answer-${ref.section}.${field.key}`}
                    />
                  ))}
                </dl>
              </div>
            );
          })}
        </section>
      ))}

      {documents && documents.slots.length > 0 && (
        <section className="space-y-2" data-testid="review-section-documents">
          <SectionTitle level="card-title">{documents.heading}</SectionTitle>
          <dl className="m-0">
            {documentReviewRows(documents).map((row) => (
              <div
                key={row.kind}
                className="flex flex-col gap-0.5 border-b border-border py-2 last:border-b-0 sm:flex-row sm:gap-4"
              >
                <dt className="text-body text-stevens-ink-3 sm:w-1/2">{row.label}</dt>
                {/* A file name is often one long word
                    ("IMG_20261011_123456789.jpeg"), so it may break anywhere. */}
                <dd
                  className="text-body font-medium text-foreground [overflow-wrap:anywhere] sm:w-1/2"
                  data-testid={`review-document-${row.kind}`}
                >
                  {row.files.length
                    ? row.files.map((name, i) => <span key={`${i}-${name}`} className="block">{name}</span>)
                    : '—'}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {retiredAnswered.length > 0 && (
        <section className="space-y-2" data-testid="review-section-retired">
          <SectionTitle level="card-title">{copy('apply.review.retired_heading')}</SectionTitle>
          <p className="text-body text-stevens-ink-3">{copy('apply.review.retired_note')}</p>
          <dl className="m-0">
            {retiredAnswered.map(({ section, field }) => (
              <AnswerRow
                key={`${section}.${field.key}`}
                field={field}
                siblings={sectionData[section as SectionKey]}
                sectionValues={sectionData}
                value={get(sectionData[section as SectionKey], field.key)}
                path={`${section}.${field.key}`}
                testId={`review-answer-${section}.${field.key}`}
                labelSuffix={retiredShownInEnglish?.(`${section}.${field.key}`) ? <ShownInEnglish /> : null}
              />
            ))}
          </dl>
        </section>
      )}
    </div>
  );
}
